import React from "react";
import { Box, Grid, Link, Typography } from "@mui/material";
import { cardColors, cardStyle, dataRowStyle } from "../../styles/cardStyles";

/**
 * Component for displaying disease association information
 * @param {Object} props - Component props
 * @param {Object} props.variant - Variant data object
 * @return {JSX.Element} - Rendered component
 */
function DiseaseCard({ variant }) {
  if (!variant) return null;

  // Parse clinical significance - handle both array and string formats
  const getClinicalSignificance = () => {
    if (!variant.clinical_significance) return [];

    // If it's already an array, return it
    if (Array.isArray(variant.clinical_significance)) {
      return variant.clinical_significance.filter(
        (item) => item && item !== "0"
      );
    }

    // If it's a string, try to parse or split
    if (typeof variant.clinical_significance === "string") {
      // Handle comma-separated values
      const items = variant.clinical_significance
        .split(",")
        .map((item) => item.trim());
      return items.filter((item) => item && item !== "0");
    }

    return [];
  };

  const clinicalSignificanceList = getClinicalSignificance();

  return (
    <Box sx={cardStyle(cardColors.disease, true)}>
      <Box
        sx={{
          background: cardColors.disease.header,
          p: 1,
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
        }}
      >
        <Typography
          color={cardColors.disease.headerText}
          variant="subtitle2"
          fontWeight="bold"
          align="center"
        >
          Disease Associations
        </Typography>
      </Box>

      <Grid container>
        {/* Clinical Significance Section */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{ borderRight: { xs: "none", md: "1px solid #e8d9c6" } }}
        >
          <Box
            sx={{
              ...dataRowStyle,
              height: "100%",
              alignItems: "flex-start",
            }}
          >
            <Typography
              variant="body2"
              color="#555555"
              fontWeight="medium"
              sx={{ mb: 0.5 }}
            >
              Clinvar:
            </Typography>
            {clinicalSignificanceList.length > 0 ? (
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.5,
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                {clinicalSignificanceList.map((significance, index) => (
                  <Typography
                    component="li"
                    key={index}
                    variant="body2"
                    color="#111111"
                    sx={{
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                      hyphens: "auto",
                    }}
                  >
                    {significance}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="#111111" fontWeight="bold">
                0
              </Typography>
            )}
          </Box>
        </Grid>

        {/* OMIM Section */}
        <Grid
          item
          xs={12}
          md={2}
          sx={{
            borderRight: { xs: "none", md: "1px solid #e8d9c6" },
            borderTop: { xs: "1px solid #e8d9c6", md: "none" },
          }}
        >
          <Box
            sx={{
              ...dataRowStyle,
              height: "100%",
              alignItems: "flex-start",
            }}
          >
            <Typography variant="body2" color="#555555" fontWeight="medium">
              OMIM:
            </Typography>
            {variant.omim && variant.omim !== "0" ? (
              <Link
                href={`https://www.omim.org/entry/${variant.omim}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textDecoration: "none",
                  color: "#1976d2",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {variant.omim}
              </Link>
            ) : (
              <Typography variant="body2" color="#111111" fontWeight="bold">
                0
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Orphanet Section */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{ borderTop: { xs: "1px solid #e8d9c6", md: "none" } }}
        >
          <Box
            sx={{
              ...dataRowStyle,
              height: "100%",
              alignItems: "flex-start",
            }}
          >
            <Typography
              variant="body2"
              color="#555555"
              fontWeight="medium"
              sx={{ alignSelf: "flex-start", pt: 0.5 }}
            >
              Orphanet:
            </Typography>
            {variant.orpha_info ? (
              <Box sx={{ display: "flex", flexDirection: "column", ml: 2 }}>
                {(() => {
                  const parts = variant.orpha_info.split("|");
                  const fields = {
                    orphaId: parts[0],
                    disease: parts[1],
                    prevalence: parts[2],
                    inheritance: parts[3],
                    onsetAge: parts[4],
                  };

                  return (
                    <>
                      {fields.orphaId && fields.orphaId !== "0" ? (
                        <Link
                          href={`https://www.orpha.net/en/disease/detail/${fields.orphaId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            textDecoration: "none",
                            color: "#1976d2",
                            fontWeight: "bold",
                            fontSize: "0.875rem",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {fields.orphaId}
                        </Link>
                      ) : (
                        <Typography
                          variant="body2"
                          color="#111111"
                          fontWeight="bold"
                        >
                          {fields.orphaId}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: "#e3f2fd",
                            p: 0.5,
                            borderRadius: 1,
                            fontSize: "0.7rem",
                          }}
                        >
                          {fields.disease}
                        </Box>
                        <Box
                          sx={{
                            backgroundColor: "#e8f5e9",
                            p: 0.5,
                            borderRadius: 1,
                            fontSize: "0.7rem",
                          }}
                        >
                          {fields.prevalence}
                        </Box>
                        <Box
                          sx={{
                            backgroundColor: "#fff8e1",
                            p: 0.5,
                            borderRadius: 1,
                            fontSize: "0.7rem",
                          }}
                        >
                          {fields.onsetAge.replace(/<br>/g, ", ")}
                        </Box>
                        {fields.inheritance && (
                          <Box
                            sx={{
                              backgroundColor: "#fce4ec",
                              p: 0.5,
                              borderRadius: 1,
                              fontSize: "0.7rem",
                            }}
                          >
                            {fields.inheritance}
                          </Box>
                        )}
                      </Box>
                    </>
                  );
                })()}
              </Box>
            ) : (
              <Typography variant="body2" color="#111111" fontWeight="bold">
                0
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default DiseaseCard;
